import React, { useState, useEffect } from 'react';
import { useStore } from '../store';
import { Menu, Upload, BarChart2, Settings, PieChart, FileDown, Home, RefreshCw, BookOpen, X } from 'lucide-react';

export function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { 
    currentPage, 
    setCurrentPage, 
    hasFile, 
    hasAxes, 
    hasScaleMapping, 
    hasAnalysisResults,
    clearAllData 
  } = useStore();

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && isOpen) {
        const sidebar = document.getElementById('sidebar');
        const menuButton = document.getElementById('menu-button');
        
        if (sidebar && menuButton && 
            !sidebar.contains(event.target as Node) && 
            !menuButton.contains(event.target as Node)) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isOpen]);

  const navItems = [
    { id: 'file_upload', label: 'رفع الملف', icon: Upload, enabled: true },
    { id: 'axis_management', label: 'إدارة المحاور', icon: Menu, enabled: hasFile() },
    { id: 'scale_config', label: 'إعداد المقياس', icon: Settings, enabled: hasFile() && hasAxes() },
    { id: 'analysis', label: 'تحليل البيانات', icon: BarChart2, enabled: hasFile() && hasAxes() && hasScaleMapping() },
    { id: 'dashboard', label: 'لوحة المعلومات', icon: PieChart, enabled: hasAnalysisResults },
    { id: 'export', label: 'تصدير النتائج', icon: FileDown, enabled: hasAnalysisResults },
    { id: 'instructions', label: 'معلومات النظام', icon: BookOpen, enabled: true }
  ];

  const handleClearData = () => {
    if (window.confirm('هل أنت متأكد من رغبتك في مسح جميع البيانات والبدء من جديد؟')) {
      clearAllData();
      setCurrentPage('file_upload');
      if (isMobile) setIsOpen(false);
    }
  };

  const handleNavClick = (pageId: string) => {
    setCurrentPage(pageId);
    // Close mobile menu when navigating
    if (isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile menu button - Enhanced with better positioning */}
      <button
        id="menu-button"
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden fixed top-4 right-4 z-50 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 backdrop-blur-sm"
        aria-label="فتح القائمة"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Overlay for mobile - Enhanced */}
      {isOpen && isMobile && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar - Enhanced responsiveness */}
      <div 
        id="sidebar"
        className={`
          h-screen bg-white/95 backdrop-blur-lg shadow-2xl fixed right-0 top-0 p-4 flex flex-col z-40 transition-all duration-300 ease-in-out border-l border-gray-200/50
          ${isMobile 
            ? `w-80 ${isOpen ? 'translate-x-0' : 'translate-x-full'}` 
            : 'w-64 translate-x-0'
          }
        `} 
        dir="rtl"
      >
        {/* Header - Enhanced */}
        <div className="flex items-center gap-3 mb-8 justify-end p-2">
          <div className="text-right">
            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              تحليل الاستبانات
            </h1>
            <p className="text-xs text-gray-500 mt-1">نظام تحليل متقدم</p>
          </div>
          <div className="p-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg">
            <Home className="w-6 h-6 text-blue-600" />
          </div>
        </div>

        {/* Navigation - Enhanced */}
        <nav className="flex-1 overflow-y-auto">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.id)}
                  disabled={!item.enabled}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl text-right justify-end transition-all duration-200 group
                    ${currentPage === item.id
                      ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600 border border-blue-200 shadow-sm'
                      : item.enabled
                      ? 'text-gray-600 hover:bg-gray-50 hover:shadow-sm'
                      : 'text-gray-300 cursor-not-allowed opacity-50'
                    }
                  `}
                >
                  <span className="font-medium text-right text-sm">{item.label}</span>
                  <div className={`p-1 rounded-lg transition-colors ${
                    currentPage === item.id 
                      ? 'bg-blue-100' 
                      : item.enabled 
                      ? 'group-hover:bg-gray-100' 
                      : ''
                  }`}>
                    <item.icon className="w-4 h-4" />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer Actions - Enhanced */}
        <div className="pt-4 mt-auto border-t border-gray-200/50 space-y-4">
          <button
            onClick={handleClearData}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-right justify-end text-red-600 hover:bg-red-50 hover:shadow-sm transition-all duration-200 group"
          >
            <span className="font-medium text-right text-sm">بدء تحليل جديد</span>
            <div className="p-1 rounded-lg group-hover:bg-red-100 transition-colors">
              <RefreshCw className="w-4 h-4" />
            </div>
          </button>

          {/* Developer Credit - Enhanced */}
          <div className="text-xs text-gray-500 text-right p-3 bg-gray-50/50 rounded-lg">
            <div className="font-medium text-gray-700">أسامة صبري الطيطي</div>
            <div className="mt-1">مركز التعلم الإلكتروني</div>
            <div>جامعة الإسراء</div>
          </div>
        </div>
      </div>
    </>
  );
}